"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Icon, type IconName } from "@/components/ui/icon";
import { NEUROSCALE_LINKS } from "@/lib/neuroscale";

/* ─────────────────────────────────────────────────────────────────────────────
 * CONFIGURATION
 *
 * The banner is fed by the Better Stack announcement widget API — publish or
 * unpublish the announcement in Better Stack Uptime and it appears/disappears
 * here (checked on load and every POLL_INTERVAL_MS).
 *
 * LOCAL_BANNER is a manual override for emergencies (e.g. Better Stack itself
 * is down): set `enabled: true` and it takes precedence over the remote one.
 * ────────────────────────────────────────────────────────────────────────── */

const BETTER_STACK_ANNOUNCEMENT_ID = "246253";
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const DISMISSED_STORAGE_KEY = "betterstack-announcement-dismissed";

const LOCAL_BANNER = {
  enabled: false,
  message: "",
  icon: "IconHammer" as IconName,
};

/* ── Better Stack announcement fetch ────────────────────────────────────── */

type Announcement = {
  message: string;
  link: string | null;
  /** Content hash from Better Stack — used to scope dismissal to this announcement. */
  digest: string;
};

async function fetchAnnouncement(): Promise<Announcement | null> {
  const res = await fetch(
    `https://uptime.betterstack.com/widgets/announcement?id=${BETTER_STACK_ANNOUNCEMENT_ID}`,
  );
  if (!res.ok) return null;

  const json: {
    success?: boolean;
    hidden?: boolean;
    html?: string;
    digest?: string;
    link?: string | null;
  } = await res.json();

  if (!json.success || json.hidden || !json.html || !json.digest) return null;

  // The API returns the widget's markup; extract plain text so we render it
  // with our own styling and never inject their HTML.
  const doc = new DOMParser().parseFromString(json.html, "text/html");
  const message = doc
    .querySelector(".betteruptime-announcement__message")
    ?.textContent?.trim();
  if (!message) return null;

  return { message, link: json.link || null, digest: json.digest };
}

/* ── Component ──────────────────────────────────────────────────────────── */

export function MaintenanceBanner() {
  const ref = useRef<HTMLDivElement>(null);
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [dismissedDigest, setDismissedDigest] = useState<string | null>(() =>
    typeof window === "undefined"
      ? null
      : localStorage.getItem(DISMISSED_STORAGE_KEY),
  );

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetchAnnouncement()
        .then((a) => {
          if (!cancelled) setAnnouncement(a);
        })
        .catch(() => {
          // Network/parse failures just mean no banner.
        });

    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const remoteVisible =
    announcement !== null && announcement.digest !== dismissedDigest;
  const visible = LOCAL_BANNER.enabled || remoteVisible;

  useEffect(() => {
    if (!visible || !ref.current) return;

    const el = ref.current;
    const update = () => {
      document.documentElement.style.setProperty(
        "--banner-offset",
        `${el.offsetHeight}px`,
      );
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--banner-offset");
    };
  }, [visible]);

  const dismiss = useCallback(() => {
    if (!announcement) return;
    localStorage.setItem(DISMISSED_STORAGE_KEY, announcement.digest);
    setDismissedDigest(announcement.digest);
  }, [announcement]);

  if (!visible) return null;

  const isLocal = LOCAL_BANNER.enabled;
  const message = isLocal ? LOCAL_BANNER.message : announcement!.message;
  const icon: IconName = isLocal ? LOCAL_BANNER.icon : "IconMegaphone";
  const link = (!isLocal && announcement!.link) || NEUROSCALE_LINKS.status;

  return (
    <div
      ref={ref}
      className="flex w-full shrink-0 items-center justify-center gap-2 border-b border-[#60A5FA]/20 px-4 py-2 text-[13px] font-medium leading-snug"
      style={{ backgroundColor: "rgba(96, 165, 250, 0.10)", color: "#60A5FA" }}
    >
      <Icon name={icon} size={16} className={isLocal ? "-scale-x-100" : undefined} />
      <span>{message}</span>
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="ml-1 inline-flex items-center gap-1 underline decoration-[#60A5FA]/40 underline-offset-2 transition-colors hover:text-[#93C5FD]"
      >
        Status
        <Icon name="IconArrowUpRight" size={14} />
      </a>
      {!isLocal && (
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss announcement"
          className="ml-2 inline-flex cursor-pointer items-center rounded p-0.5 transition-colors hover:text-[#93C5FD]"
        >
          <Icon name="IconCrossMedium" size={14} />
        </button>
      )}
    </div>
  );
}
